import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Chip, Stack, Button, CircularProgress, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  Grid, Checkbox, Snackbar, Alert, IconButton, Tooltip
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import { makeRequest } from '../../../assets/functions_helper';

// Colores semánticos
const statusColors = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'error',
  cancelled: 'default'
};

function ReservationList({ restaurantId }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals state
  const [confirmModal, setConfirmModal] = useState({ open: false, res: null });
  const [rejectModal, setRejectModal] = useState({ open: false, res: null });
  const [cancelModal, setCancelModal] = useState({ open: false, res: null });

  // Acciones en curso (loading buttons)
  const [isProcessing, setIsProcessing] = useState(false);

  // States para Modal Confirmar
  const [availability,          setAvailability]          = useState([]);
  const [combinationSuggestions,setCombinationSuggestions] = useState([]);
  const [selectedTables,        setSelectedTables]        = useState([]);
  const [confirmMessage,        setConfirmMessage]        = useState('');
  const [conflictError,         setConflictError]         = useState(null);
  
  // States para Modal Rechazar
  const [rejectionReason, setRejectionReason] = useState('');

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // States para Modal Editar Notas
  const [notesModal, setNotesModal] = useState({ open: false, res: null });
  const [editingNotes, setEditingNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Fetch Data logic
  const fetchReservations = () => {
    if (!restaurantId) return;
    setLoading(true);
    const publicId = localStorage.getItem('restaurant_public_id') || restaurantId;
    makeRequest('GET', `reservations?restaurant_public_id=${publicId}`, null, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true)
      .then((data) => {
        // El endpoint devuelve { total, page, results: [...] }
        const list = Array.isArray(data) ? data : (data?.results || []);
        setReservations(list);
      })
      .catch((err) => console.error('Error fetching reservations:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReservations();

    const handleReload = (e) => {
      if (e.detail?.restaurantId && e.detail.restaurantId === restaurantId) {
        fetchReservations();
      }
    };

    window.addEventListener('reload_reservations', handleReload);
    return () => {
      window.removeEventListener('reload_reservations', handleReload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  // --- Handlers Modal Confirmar ---
  const openConfirmModal = (res) => {
    setConfirmModal({ open: true, res });
    setConflictError(null);
    setConfirmMessage('');
    setSelectedTables([]);
    setAvailability([]);
    setCombinationSuggestions([]);
    setIsProcessing(true);

    const queryParams = new URLSearchParams({
      restaurant_public_id: localStorage.getItem('restaurant_public_id') || restaurantId,
      datetime: res.requested_datetime,
      guest_count: res.guest_count,
    });

    makeRequest('GET', `reservations/availability?${queryParams.toString()}`, null, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true)
      .then(data => {
         setAvailability(data?.available_tables || []);
         setCombinationSuggestions(data?.combination_suggestions || []);
      })
      .catch(() => setConflictError('Error al cargar mesas disponibles.'))
      .finally(() => setIsProcessing(false));
  };



  const handleConfirmSubmit = async () => {
    setIsProcessing(true);
    setConflictError(null);
    try {
      await makeRequest('POST', `reservations/${confirmModal.res.id}/confirm`, {
         table_ids: selectedTables,
         confirmation_message: confirmMessage
      }, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true);
      
      setSnack({ open: true, severity: 'success', message: `Recuerda llamar o enviar mensaje al cliente al ${confirmModal.res.customer_phone} para avisarle que su reserva está confirmada.` });
      setConfirmModal({ open: false, res: null });
    } catch (err) {
      if (err.error_code === '409' || err.status === 409) {
         setConflictError('Esta mesa ya está reservada en un rango de 4 horas para este horario.');
      } else {
         setConflictError('Ha ocurrido un error al confirmar la reserva.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handlers Modal Rechazar ---
  const openRejectModal = (res) => {
    setRejectionReason('');
    setRejectModal({ open: true, res });
  };

  const handleRejectSubmit = async () => {
    setIsProcessing(true);
    try {
      await makeRequest('POST', `reservations/${rejectModal.res.id}/reject`, {
         rejection_reason: rejectionReason
      }, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true);
      
      setSnack({ open: true, severity: 'info', message: `Contacta al cliente al ${rejectModal.res.customer_phone} informando el motivo del rechazo.` });
      setRejectModal({ open: false, res: null });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: 'error', message: 'No se pudo rechazar la reserva.' });
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Handlers Modal Cancelar ---
  const openCancelModal = (res) => {
    setCancelModal({ open: true, res });
  };

  const handleCancelSubmit = async () => {
    setIsProcessing(true);
    try {
      await makeRequest('POST', `reservations/${cancelModal.res.id}/cancel`, null, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true);
      setSnack({ open: true, severity: 'success', message: 'Mesa liberada correctamente.' });
      setCancelModal({ open: false, res: null });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: 'error', message: 'No se pudo cancelar la reserva.' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Normaliza el ID de mesa sin importar qué campo devuelva el backend
  const tableId = (t) => t?.id ?? t?.table_id ?? t?.table_number ?? null;

  const toggleTable = (t) => {
    const id = tableId(t);
    setSelectedTables(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const currentCapacity = selectedTables.reduce((acc, currentId) => {
    const t = availability.find(x => tableId(x) === currentId);
    return acc + (t?.seat_count || t?.capacity || 0);
  }, 0);

  // --- Handlers Modal Editar Notas ---
  const openNotesModal = (res) => {
    setEditingNotes(res.notes || '');
    setNotesModal({ open: true, res });
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await makeRequest('PATCH', `reservations/${notesModal.res.id}/notes`, {
        notes: editingNotes
      }, 'application/json', localStorage.getItem('token'), localStorage.getItem('tokenType'), null, true);

      // Actualizar estado local inmediatamente
      setReservations(prev => prev.map(r =>
        r.id === notesModal.res.id ? { ...r, notes: editingNotes } : r
      ));
      setSnack({ open: true, severity: 'success', message: 'Notas actualizadas correctamente.' });
      setNotesModal({ open: false, res: null });
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: 'error', message: 'No se pudieron guardar las notas.' });
    } finally {
      setIsSavingNotes(false);
    }
  };

  const formatDateLocally = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return isNaN(d.getTime()) ? isoString : d.toLocaleString('es-ES', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const publicReservationLink = `${window.location.origin}/reserve/${localStorage.getItem('restaurant_public_id') || restaurantId}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicReservationLink);
    setSnack({ open: true, message: 'Enlace copiado al portapapeles', severity: 'success' });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: 'Como, sans-serif' }}>
            Gestión de Reservas
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mt: 1 }}>
            Administra las solicitudes entrantes en tiempo real.
          </Typography>
        </Box>

        {/* Link público */}
        <Paper elevation={0} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: '16px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 36, height: 36, borderRadius: '10px', bgcolor: '#e0e7ff', color: '#4f46e5',
              display: 'flex', alignItems: 'center', justifyContent: 'center' 
            }}>
              <QrCode2RoundedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mb: 0.2 }}>URL DE RESERVAS PÚBLICAS</Typography>
              <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, maxWidth: { xs: 200, sm: 300, md: 350 }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {publicReservationLink}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Copiar enlace">
              <IconButton onClick={copyToClipboard} size="small" sx={{ color: '#475569' }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Opcional: abrir en pestaña nueva */}
            <Tooltip title="Abrir enlace en pestaña nueva">
              <IconButton onClick={() => window.open(publicReservationLink, '_blank')} size="small" sx={{ color: '#475569' }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Box>

      {loading ? null : (
        <Stack spacing={2}>
          {reservations.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', color: '#64748b', borderRadius: '16px', border: '1px dashed #cbd5e1' }} elevation={0}>
              No tienes reservas activas por ahora.
            </Paper>
          ) : (
            reservations.map((res) => {
              // Lógica de orfandad (Phase 2 compat)
              const isOrphan = res.status === 'confirmed' && (!res.assigned_table_ids || res.assigned_table_ids.length === 0);

              return (
                <Paper key={res.id} sx={{ p: 3, borderRadius: '16px', border: isOrphan ? '2px solid #ef4444' : '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }} elevation={0}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                      {res.customer_name || 'Cliente sin nombre'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                      🗓 {formatDateLocally(res.requested_datetime)} • 👥 {res.guest_count} personas
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#0ea5e9', fontWeight: 600 }}>
                      📞 <a href={`tel:${res.customer_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{res.customer_phone}</a>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {isOrphan ? (
                      <Chip icon={<WarningAmberRoundedIcon />} label="Mesa eliminada — requiere reasignación" color="error" variant="filled" sx={{ fontWeight: 'bold' }} />
                    ) : (
                      <Chip label={res.status.toUpperCase()} color={statusColors[res.status] || 'default'} size="small" sx={{ fontWeight: 800, letterSpacing: 1 }} />
                    )}

                    <Stack direction="row" spacing={1}>
                      {res.status === 'pending' && (
                        <>
                          <Button variant="contained" color="success" onClick={() => openConfirmModal(res)} disableElevation sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                            Confirmar
                          </Button>
                          <Button variant="outlined" color="error" onClick={() => openRejectModal(res)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                            Rechazar
                          </Button>
                        </>
                      )}

                      {res.status === 'confirmed' && (
                         <Button variant="outlined" color="inherit" onClick={() => openCancelModal(res)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                           Cancelar
                         </Button>
                      )}
                      
                      {isOrphan && (
                        <Button variant="contained" color="error" size="small" onClick={() => openConfirmModal(res)}>
                          Reasignar Mesa
                        </Button>
                      )}
                    </Stack>
                  </Box>
                  </Box>

                  {/* ── Notas del cliente ── */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                    {res.notes ? (
                      <Paper elevation={0} sx={{ flex: 1, p: 1.5, bgcolor: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <StickyNote2RoundedIcon sx={{ color: '#b45309', fontSize: 18, mt: 0.2 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 800, display: 'block', mb: 0.2 }}>NOTAS DEL CLIENTE</Typography>
                          <Typography variant="body2" sx={{ color: '#78350f', whiteSpace: 'pre-line' }}>{res.notes}</Typography>
                        </Box>
                        <Tooltip title="Editar notas">
                          <IconButton size="small" onClick={() => openNotesModal(res)} sx={{ color: '#b45309' }}>
                            <EditNoteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    ) : (
                      <Tooltip title="Agregar notas">
                        <IconButton size="small" onClick={() => openNotesModal(res)} sx={{ color: '#94a3b8', '&:hover': { color: '#475569' } }}>
                          <EditNoteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Paper>
              );
            })
          )}
        </Stack>
      )}

      {/* --- MODAL CONFIRMAR --- */}
      <Dialog open={confirmModal.open} onClose={() => !isProcessing && setConfirmModal({open:false, res:null})} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar Reserva</DialogTitle>
        <DialogContent dividers>
          {/* ── Detalle de la solicitud para que el mesero valide con el cliente ── */}
          {confirmModal.res && (
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 1.5 }}>
                📋 Detalle de la Solicitud
              </Typography>
              <Stack spacing={0.8}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', minWidth: 90 }}>Cliente:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{confirmModal.res.customer_name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', minWidth: 90 }}>Teléfono:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    <a href={`tel:${confirmModal.res.customer_phone}`} style={{ color: '#0ea5e9', textDecoration: 'none' }}>
                      📞 {confirmModal.res.customer_phone}
                    </a>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', minWidth: 90 }}>Fecha y hora:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{formatDateLocally(confirmModal.res.requested_datetime)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', minWidth: 90 }}>Comensales:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>👥 {confirmModal.res.guest_count} personas</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748b', minWidth: 90 }}>Solicitud:</Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>{formatDateLocally(confirmModal.res.created_at)}</Typography>
                </Box>
                {confirmModal.res.notes && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', minWidth: 90 }}>Notas:</Typography>
                    <Paper elevation={0} sx={{ p: 1, bgcolor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', flex: 1 }}>
                      <Typography variant="body2" sx={{ color: '#78350f', whiteSpace: 'pre-line' }}>{confirmModal.res.notes}</Typography>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {isProcessing && availability.length === 0 && combinationSuggestions.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : (
            <Box>
              {/* ── Mesas individuales disponibles ── */}
              {availability.length > 0 && (
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {availability.map(table => (
                    <Grid item xs={6} key={tableId(table)}>
                       <Paper
                         elevation={0}
                         sx={{
                           p: 1.5, borderRadius: '12px', cursor: 'pointer',
                           border: selectedTables.includes(tableId(table)) ? '2px solid #22c55e' : '1px solid #e2e8f0',
                           display: 'flex', alignItems: 'center'
                         }}
                         onClick={() => toggleTable(table)}
                       >
                          <Checkbox checked={selectedTables.includes(tableId(table))} size="small" sx={{ p: 0, mr: 1 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={700}>{table.table_name ?? table.name ?? `Mesa ${tableId(table)}`}</Typography>
                            <Typography variant="caption" color="text.secondary">Aforo: {table.seat_count ?? table.capacity ?? '—'}</Typography>
                          </Box>
                       </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* ── Combinaciones sugeridas ── */}
              {combinationSuggestions.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    🔗 Combinaciones sugeridas
                    <Typography variant="caption" sx={{ color: '#94a3b8', ml: 0.5 }}>(clic para seleccionar todo el grupo)</Typography>
                  </Typography>
                  <Stack spacing={1}>
                    {combinationSuggestions.map((combo, i) => {
                      const comboIds = combo.tables || [];
                      const isSelected = comboIds.length > 0 && comboIds.every(id => selectedTables.includes(id));
                      return (
                        <Paper
                          key={i}
                          elevation={0}
                          onClick={() => setSelectedTables(isSelected ? [] : comboIds)}
                          sx={{
                            p: 2, borderRadius: '12px', cursor: 'pointer',
                            border: isSelected ? '2px solid #6366f1' : '1px solid #e2e8f0',
                            bgcolor: isSelected ? '#eef2ff' : '#fafafa',
                            transition: 'all .15s ease',
                            '&:hover': { borderColor: '#6366f1', bgcolor: '#f5f3ff' }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ color: isSelected ? '#4f46e5' : '#1e293b' }}>
                                Mesas: {comboIds.join(' + ')}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Capacidad total: {combo.total_seats} personas
                              </Typography>
                            </Box>
                            <Chip
                              label={isSelected ? 'Seleccionada ✓' : 'Seleccionar'}
                              size="small"
                              sx={{
                                bgcolor: isSelected ? '#6366f1' : 'transparent',
                                color: isSelected ? '#fff' : '#6366f1',
                                border: '1px solid #6366f1',
                                fontWeight: 700,
                              }}
                            />
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* ── Sin opciones en absoluto ── */}
              {availability.length === 0 && combinationSuggestions.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>No se encontraron mesas libres para esta capacidad y horario.</Alert>
              )}

              {selectedTables.length > 0 && currentCapacity < (confirmModal.res?.guest_count || 0) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Las mesas seleccionadas podrían no tener capacidad suficiente ({currentCapacity} vs {confirmModal.res?.guest_count} pax).
                </Alert>
              )}

              {conflictError && <Alert severity="error" sx={{ mb: 2 }}>{conflictError}</Alert>}

              <TextField 
                fullWidth label="Mensaje de confirmación (Opcional)" multiline rows={2}
                value={confirmMessage} onChange={e => setConfirmMessage(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmModal({open:false, res:null})} color="inherit" disabled={isProcessing}>Cerrar</Button>
          <Button onClick={handleConfirmSubmit} variant="contained" color="success" disabled={isProcessing || selectedTables.length === 0} disableElevation sx={{ borderRadius: '10px' }}>
            {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Reserva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- MODAL RECHAZAR --- */}
      <Dialog open={rejectModal.open} onClose={() => !isProcessing && setRejectModal({open:false, res:null})} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Rechazar Reserva</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Por favor, indica el motivo del rechazo para que quede registro.</Typography>
          <TextField 
            fullWidth required label="Motivo de Rechazo" multiline rows={3}
            value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectModal({open:false, res:null})} color="inherit" disabled={isProcessing}>Cerrar</Button>
          <Button onClick={handleRejectSubmit} variant="contained" color="error" disabled={isProcessing || !rejectionReason.trim()} disableElevation sx={{ borderRadius: '10px' }}>
            {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Rechazar Solicitud'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- MODAL CANCELAR --- */}
      <Dialog open={cancelModal.open} onClose={() => !isProcessing && setCancelModal({open:false, res:null})} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Cancelar Reserva</DialogTitle>
        <DialogContent>
          <Typography>¿Seguro que deseas cancelar esta reserva y liberar la mesa asociada?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelModal({open:false, res:null})} color="inherit" disabled={isProcessing}>Volver</Button>
          <Button onClick={handleCancelSubmit} variant="contained" color="error" disabled={isProcessing} disableElevation sx={{ borderRadius: '10px' }}>
            {isProcessing ? <CircularProgress size={24} color="inherit" /> : 'Sí, Cancelar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- MODAL EDITAR NOTAS --- */}
      <Dialog open={notesModal.open} onClose={() => !isSavingNotes && setNotesModal({open:false, res:null})} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditNoteRoundedIcon sx={{ color: '#64748b' }} />
          Editar Notas
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Agrega o modifica las notas internas de esta reserva. Envía un campo vacío para limpiarlas.
          </Typography>
          <TextField 
            fullWidth 
            label="Notas" 
            multiline 
            rows={4}
            value={editingNotes} 
            onChange={e => setEditingNotes(e.target.value)}
            placeholder="Ej. El cliente tiene alergia al gluten, mesa preferida cerca de la ventana..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setNotesModal({open:false, res:null})} color="inherit" disabled={isSavingNotes}>Cancelar</Button>
          <Button onClick={handleSaveNotes} variant="contained" disabled={isSavingNotes} disableElevation sx={{ borderRadius: '10px', bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>
            {isSavingNotes ? <CircularProgress size={24} color="inherit" /> : 'Guardar Notas'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- SNACKBAR GLBOAL --- */}
      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack(s => ({...s, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack(s => ({...s, open: false}))} severity={snack.severity} sx={{ borderRadius: '12px', fontWeight: 700 }} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ReservationList;
